import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { COUNTRY_CODES, CountryDialCode } from '@/lib/geoData';
import { getCountries } from '@/lib/geoApi';
import { Floating } from './Floating';

// Module-level cache so every PhoneInput instance shares one fetch of the
// countries master (dial codes + flags) and renders the full list, not the
// small hardcoded fallback.
let _dialCodesCache: CountryDialCode[] | null = null;

export interface PhoneValue {
  countryCode: string;  // e.g. "+91"
  number: string;       // e.g. "9876543210"
}

interface Props {
  value: PhoneValue;
  onChange: (val: PhoneValue) => void;
  placeholder?: string;
  inputClassName?: string;
  disabled?: boolean;
}

export const DEFAULT_PHONE: PhoneValue = { countryCode: '+91', number: '' };

/** Parse a stored string like "+91 9876543210" OR "+919876543210" into PhoneValue.
 *  Old data (CSV imports, API payloads) often arrived without a separator,
 *  so we match against the known country-code list (longest prefix first)
 *  before falling back to space splitting. Prevents "+91" leaking into the
 *  number field as "+919876543210". */
export function parsePhoneString(raw: string): PhoneValue {
  if (!raw) return { ...DEFAULT_PHONE };
  const trimmed = raw.trim();

  // 1. Explicit space separator — "+91 9876543210"
  const spaceMatch = trimmed.match(/^(\+\d[\d\s-]{0,5})\s+(.+)$/);
  if (spaceMatch) {
    return { countryCode: spaceMatch[1].replace(/\s+/g, ''), number: spaceMatch[2].trim() };
  }

  // 2. No separator — walk known codes, longest first, and strip.
  if (trimmed.startsWith('+')) {
    const codes = COUNTRY_CODES.map((c) => c.code).sort((a, b) => b.length - a.length);
    for (const code of codes) {
      if (trimmed.startsWith(code)) {
        const rest = trimmed.slice(code.length).replace(/^[\s-]+/, '');
        if (rest) return { countryCode: code, number: rest };
      }
    }
    // Prefix starts with "+" but no known code matched — keep the "+" prefix
    // off the number so the input doesn't double it.
    return { countryCode: '+91', number: trimmed.replace(/^\+/, '') };
  }

  // 3. Plain digits — assume default country code.
  return { countryCode: '+91', number: trimmed };
}

/** Serialize PhoneValue to a single string */
export function formatPhoneString(val: PhoneValue): string {
  if (!val.number.trim()) return '';
  return `${val.countryCode} ${val.number.trim()}`;
}

// Display a LeadPhone (or any { isdCode, phoneNumber }) as one readable string.
// The API no longer returns a combined `phone`, so all lead phone displays go
// through this. Tolerates the legacy `phone` field for safety.
export function formatLeadPhone(p: any): string {
  if (!p) return '';
  if (typeof p === 'string') return p;
  const joined = [p.isdCode, p.phoneNumber].filter(Boolean).join(' ').trim();
  return joined || p.phone || '';
}

export function PhoneInput({ value, onChange, placeholder = 'Phone number', inputClassName = '', disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Full dial-code list from the countries master (DB). Starts with the small
  // hardcoded set for instant render, then swaps to the complete list.
  const [countries, setCountries] = useState<CountryDialCode[]>(_dialCodesCache ?? COUNTRY_CODES);
  const buttonWrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (_dialCodesCache) return; // already loaded for the session
    let alive = true;
    getCountries()
      .then((rows) => {
        if (!alive || !Array.isArray(rows)) return;
        const mapped: CountryDialCode[] = rows
          .filter((r: any) => r.dialCode && String(r.dialCode).trim())
          .map((r: any) => {
            let code = String(r.dialCode).trim();
            if (!code.startsWith('+')) code = '+' + code;
            return { code, country: r.name, flag: r.flagEmoji || '🏳️' };
          });
        if (mapped.length) { _dialCodesCache = mapped; setCountries(mapped); }
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const selected: CountryDialCode =
    countries.find(c => c.code === value.countryCode && !query) ||
    countries.find(c => c.code === value.countryCode) ||
    countries[0] ||
    COUNTRY_CODES[0];

  const filtered = query.trim()
    ? countries.filter(c =>
        c.country.toLowerCase().includes(query.toLowerCase()) ||
        c.code.includes(query)
      )
    : countries;

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 10);
    else setQuery('');
  }, [open]);

  // Outside-click + Escape handled by <Floating> (below).

  const selectCode = (c: CountryDialCode) => {
    onChange({ ...value, countryCode: c.code });
    setOpen(false);
  };

  const BASE = 'h-10 border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-all';

  return (
    <div className="flex w-full">
      {/* Country code button */}
      <div ref={buttonWrapRef} className="relative flex-shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(v => !v)}
          className={`${BASE} flex items-center gap-1.5 px-2.5 rounded-l-lg border-r-0 min-w-[88px] cursor-pointer hover:bg-gray-50
            ${open ? 'ring-2 ring-[#6366F1] z-10' : ''}
            ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : ''}`}
        >
          <span className="text-base leading-none">{selected.flag}</span>
          <span className="text-xs font-medium text-[#374151]">{selected.code}</span>
          <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ml-auto ${open ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown — portalled so it escapes overflow-hidden drawers/cards. */}
        <Floating
          open={open}
          anchorRef={buttonWrapRef}
          onClose={() => setOpen(false)}
          matchWidth={false}
          className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
          style={{ width: 240, maxHeight: 280, display: 'flex', flexDirection: 'column' }}
        >
            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-shrink-0">
              <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <input
                ref={searchRef}
                className="flex-1 text-sm bg-transparent outline-none text-[#1E293B] placeholder:text-gray-400"
                placeholder="Search country or code..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Options */}
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-xs text-gray-400 text-center">No results</p>
              ) : (
                filtered.map((c, i) => (
                  <button
                    key={`${c.code}-${i}`}
                    type="button"
                    onClick={() => selectCode(c)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors
                      ${c.code === value.countryCode && c.country === selected.country
                        ? 'bg-[#6366F1] text-white'
                        : 'text-[#1E293B] hover:bg-indigo-50 hover:text-[#6366F1]'
                      }`}
                  >
                    <span className="text-base leading-none w-6 flex-shrink-0">{c.flag}</span>
                    <span className="flex-1 truncate text-xs">{c.country}</span>
                    <span className="text-xs font-mono flex-shrink-0 opacity-70">{c.code}</span>
                  </button>
                ))
              )}
            </div>
        </Floating>
      </div>

      {/* Number input — digits only, capped at E.164 max (15 digits).
          E.164 is the ITU standard: max 15 digits in the subscriber number
          (excluding country code prefix). 15 here is generous; most real
          numbers are 7-12 digits. Strips non-digits as the user types so
          paste from "(987) 654-3210" still works. */}
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        maxLength={15}
        className={`${BASE} flex-1 rounded-r-lg px-3 ${inputClassName}`}
        placeholder={placeholder}
        value={value.number}
        disabled={disabled}
        onChange={e => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
          onChange({ ...value, number: digits });
        }}
      />
    </div>
  );
}
